import React, { useState, useEffect } from 'react';

const EventRegistration = ({ eventId }) => {
  const [event, setEvent] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationData, setRegistrationData] = useState({
    custom_fields: {},
    image_rights_accepted: false,
    rules_accepted: false
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/events/${eventId}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();
      if (data.success) {
        setEvent(data.event);
        
        // Si l'utilisateur est déjà inscrit, rediriger
        if (data.user_registration) {
          window.location.href = `/events/${eventId}`;
        }
      } else {
        alert(`❌ ${data.message}`);
        window.location.href = `/events/${eventId}`;
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setRegistrationData(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [fieldName]: value
      }
    }));
  };

  const submitRegistration = async () => {
    const response = await fetch(`/api/v1/events/${eventId}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify(registrationData)
    });
    return response.json();
  };

  const handleSubmitRegistration = async () => {
    setSubmitting(true);
    try {
      const data = await submitRegistration();
      if (data.success) {
        if (data.registration.payment_amount > 0 && !data.registration.paid) {
          // Paiement requis → afficher l'étape de paiement
          setPaymentUrl(data.payment_url);
          setCurrentStep(3);
        } else {
          // Inscription gratuite ou déjà payée
          setCurrentStep(4);
        }
      } else {
        alert(`❌ ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      alert('❌ Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayLater = async () => {
    setSubmitting(true);
    try {
      const data = await submitRegistration();
      if (data.success) {
        window.location.href = `/events/${eventId}`;
      } else {
        alert(`❌ ${data.message}`);
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      alert('❌ Erreur lors de l\'inscription');
      setSubmitting(false);
    }
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!registrationData.image_rights_accepted) {
        alert('Vous devez accepter le droit à l\'image pour continuer');
        return false;
      }
      if (!registrationData.rules_accepted) {
        alert('Vous devez accepter le règlement intérieur pour continuer');
        return false;
      }
    }
    if (currentStep === 2) {
      // Valider les champs obligatoires
      if (event.custom_fields) {
        for (const field of event.custom_fields) {
          if (field.required && !registrationData.custom_fields[field.name]) {
            alert(`Le champ "${field.label}" est obligatoire`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center p-5">
        <h3>❌ Événement non trouvé</h3>
        <button className="btn btn-primary" onClick={() => window.location.href = '/events'}>
          ← Retour aux événements
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid event-mobile-shell event-registration-page">
      <div className="event-registration">
        {/* Progress Bar */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row text-center event-registration-progress-row">
              <div className="col">
                <div className={`badge ${currentStep >= 1 ? 'bg-primary' : 'bg-secondary'} mb-2`}>1</div>
                <div className={`small ${currentStep >= 1 ? 'text-primary' : 'text-muted'}`}>
                  Vérification
                </div>
              </div>
            <div className="col">
              <div className={`badge ${currentStep >= 2 ? 'bg-primary' : 'bg-secondary'} mb-2`}>2</div>
              <div className={`small ${currentStep >= 2 ? 'text-primary' : 'text-muted'}`}>
                Informations
              </div>
            </div>
            <div className="col">
              <div className={`badge ${currentStep >= 3 ? 'bg-primary' : 'bg-secondary'} mb-2`}>3</div>
              <div className={`small ${currentStep >= 3 ? 'text-primary' : 'text-muted'}`}>
                Paiement
              </div>
            </div>
            <div className="col">
              <div className={`badge ${currentStep >= 4 ? 'bg-success' : 'bg-secondary'} mb-2`}>✓</div>
              <div className={`small ${currentStep >= 4 ? 'text-success' : 'text-muted'}`}>
                Confirmation
              </div>
            </div>
          </div>
          <div className="progress mt-3" style={{ height: '4px' }}>
            <div
              className="progress-bar"
              style={{ width: `${(currentStep - 1) * 33.33}%` }}
            />
          </div>
        </div>
      </div>

      {/* Étape 1: Vérification */}
      {currentStep === 1 && (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">📋 Vérification de l'événement</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-8">
                <h4>{event.title}</h4>
                <p className="text-muted mb-3">{event.description}</p>
                
                <div className="row">
                  <div className="col-sm-6 mb-3">
                    <strong>📅 Date :</strong><br />
                    {formatDate(event.start_date)}
                  </div>
                  <div className="col-sm-6 mb-3">
                    <strong>📍 Lieu :</strong><br />
                    {event.location}
                  </div>
                  <div className="col-sm-6 mb-3">
                    <strong>👥 Participants :</strong><br />
                    {event.registered_count}/{event.capacity}
                    {event.spots_remaining === 0 && (
                      <span className="text-warning ms-2">⚠️ Liste d'attente</span>
                    )}
                  </div>
                  <div className="col-sm-6 mb-3">
                    <strong>💰 Prix pour vous :</strong><br />
                    <span className="h5 text-primary">
                      {event.user_price === 0 ? 'Gratuit' : `${event.user_price}€`}
                    </span>
                  </div>
                </div>

                {event.spots_remaining === 0 && (
                  <div className="alert alert-warning">
                    ⚠️ <strong>Événement complet</strong><br />
                    Vous serez ajouté à la liste d'attente et serez notifié en cas de désistement.
                  </div>
                )}

                {/* Cases à cocher légales */}
                <div className="mt-4 p-3" style={{ background: '#f8f0f2', borderRadius: '10px', border: '1px solid rgba(124,40,55,0.15)' }}>
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="imageRights"
                      checked={registrationData.image_rights_accepted}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, image_rights_accepted: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="imageRights">
                      J'accepte le <a href="/mentions-legales/droit-image" target="_blank" rel="noopener noreferrer" style={{ color: '#7c2837', fontWeight: 600 }}>droit à l'image</a> <span className="text-danger">*</span>
                    </label>
                    <div className="form-text" style={{ fontSize: '12px' }}>
                      En cochant cette case, vous autorisez l'association à utiliser les photos et vidéos prises lors de l'événement.
                    </div>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="eventRules"
                      checked={registrationData.rules_accepted}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, rules_accepted: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="eventRules">
                      J'accepte le <a href="/mentions-legales/reglement-interieur" target="_blank" rel="noopener noreferrer" style={{ color: '#7c2837', fontWeight: 600 }}>règlement intérieur des événements</a> <span className="text-danger">*</span>
                    </label>
                    <div className="form-text" style={{ fontSize: '12px' }}>
                      En cochant cette case, vous vous engagez à respecter le règlement intérieur de l'association.
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h6>Résumé de votre inscription</h6>
                    <div className="h4 text-primary">
                      {event.user_price === 0 ? 'Gratuit' : `${event.user_price}€`}
                    </div>
                    {event.user_price > 0 && (
                      <small className="text-muted">Paiement sécurisé</small>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between mt-4">
              <a href={`/events/${eventId}`} className="btn btn-secondary">
                ← Retour à l'événement
              </a>
              <button className="btn btn-primary" onClick={nextStep}>
                Continuer l'inscription →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Étape 2: Informations complémentaires */}
      {currentStep === 2 && (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">📝 Informations complémentaires</h5>
          </div>
          <div className="card-body">
            {event.custom_fields && event.custom_fields.length > 0 ? (
              <>
                <p className="text-muted mb-4">
                  Merci de compléter les informations suivantes pour finaliser votre inscription :
                </p>
                
                {event.custom_fields.map((field, index) => (
                  <div key={index} className="mb-4">
                    <label className="form-label">
                      {field.label}
                      {field.required && <span className="text-danger">*</span>}
                    </label>
                    
                    {field.type === 'text' && (
                      <input
                        type="text"
                        className="form-control"
                        value={registrationData.custom_fields[field.name] || ''}
                        onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                        required={field.required}
                        placeholder={field.placeholder}
                      />
                    )}
                    
                    {field.type === 'textarea' && (
                      <textarea
                        className="form-control"
                        rows="3"
                        value={registrationData.custom_fields[field.name] || ''}
                        onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                        required={field.required}
                        placeholder={field.placeholder}
                      />
                    )}
                    
                    {field.type === 'select' && (
                      <select
                        className="form-select"
                        value={registrationData.custom_fields[field.name] || ''}
                        onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                        required={field.required}
                      >
                        <option value="">Choisir...</option>
                        {field.options.map((option, optIndex) => {
                          const optVal = typeof option === 'string' ? option : (option.value || option.label);
                          const optLabel = typeof option === 'string' ? option : (option.label || option.value);
                          return (
                            <option key={optIndex} value={optVal}>
                              {optLabel}
                            </option>
                          );
                        })}
                      </select>
                    )}
                    
                    {field.type === 'checkbox' && (
                      field.options && field.options.length > 0 ? (
                        <div>
                          {field.options.map((option, optIndex) => {
                            const optVal = typeof option === 'string' ? option : (option.label || option.value);
                            const currentValues = Array.isArray(registrationData.custom_fields[field.name]) ? registrationData.custom_fields[field.name] : [];
                            const isChecked = currentValues.includes(optVal);
                            return (
                              <div className="form-check" key={optIndex} style={{ marginBottom: 6 }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const newValues = e.target.checked
                                      ? [...currentValues, optVal]
                                      : currentValues.filter(v => v !== optVal);
                                    handleCustomFieldChange(field.name, newValues);
                                  }}
                                />
                                <label className="form-check-label">{optVal}</label>
                              </div>
                            );
                          })}
                          {field.allow_other && (() => {
                            const currentValues = Array.isArray(registrationData.custom_fields[field.name]) ? registrationData.custom_fields[field.name] : [];
                            const knownOptions = field.options.map(o => typeof o === 'string' ? o : (o.label || o.value));
                            const otherValue = currentValues.find(v => !knownOptions.includes(v));
                            const otherChecked = otherValue !== undefined;
                            return (
                              <div style={{ marginTop: 4 }}>
                                <div className="form-check" style={{ marginBottom: 4 }}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={otherChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        handleCustomFieldChange(field.name, [...currentValues, '']);
                                      } else {
                                        handleCustomFieldChange(field.name, currentValues.filter(v => knownOptions.includes(v)));
                                      }
                                    }}
                                  />
                                  <label className="form-check-label">Autre</label>
                                </div>
                                {otherChecked && (
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ marginLeft: 24, maxWidth: 300 }}
                                    placeholder="Précisez..."
                                    value={otherValue || ''}
                                    onChange={(e) => {
                                      const newValues = currentValues.filter(v => knownOptions.includes(v));
                                      newValues.push(e.target.value);
                                      handleCustomFieldChange(field.name, newValues);
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={registrationData.custom_fields[field.name] || false}
                            onChange={(e) => handleCustomFieldChange(field.name, e.target.checked)}
                            required={field.required}
                          />
                          <label className="form-check-label">
                            {field.placeholder || 'Oui'}
                          </label>
                        </div>
                      )
                    )}
                    
                    {field.help && <div className="form-text">{field.help}</div>}
                  </div>
                ))}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">Aucune information complémentaire requise.</p>
              </div>
            )}

            <div className="d-flex justify-content-between mt-4">
              <button className="btn btn-secondary" onClick={() => setCurrentStep(1)}>
                ← Précédent
              </button>
              <button 
                className="btn btn-primary" 
                onClick={event.user_price > 0 ? nextStep : handleSubmitRegistration}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Inscription...
                  </>
                ) : event.user_price > 0 ? (
                  'Procéder au paiement →'
                ) : (
                  'Finaliser l\'inscription →'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Étape 3: Paiement */}
      {currentStep === 3 && (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">💳 Paiement sécurisé</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-8">
                <div className="alert alert-info">
                  <h6>🔒 Paiement sécurisé</h6>
                  <p className="mb-2">
                    Votre inscription a été enregistrée ! Pour finaliser votre participation, 
                    veuillez procéder au paiement sécurisé de <strong>{event.user_price}€</strong>.
                  </p>
                  <p className="mb-0">
                    Vous serez redirigé vers notre partenaire de paiement sécurisé.
                  </p>
                </div>

                <div className="d-grid gap-2">
                  <button
                    className="btn btn-success btn-lg"
                    onClick={() => window.location.href = paymentUrl}
                    disabled={!paymentUrl}
                  >
                    🔒 Payer {event.user_price}€ maintenant
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handlePayLater}
                    disabled={submitting}
                  >
                    {submitting ? 'Traitement...' : 'Payer plus tard (inscription en attente)'}
                  </button>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6>Récapitulatif</h6>
                    <div className="d-flex justify-content-between">
                      <span>Événement :</span>
                      <strong>{event.title}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Prix :</span>
                      <strong>{event.user_price}€</strong>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between">
                      <span><strong>Total :</strong></span>
                      <strong className="text-primary">{event.user_price}€</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Étape 4: Confirmation */}
      {currentStep === 4 && (
        <div className="card">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">✅ Inscription confirmée !</h5>
          </div>
          <div className="card-body text-center">
            <div className="py-4">
              <div className="display-1 text-success">🎉</div>
              <h3 className="mt-3">Félicitations !</h3>
              <p className="lead">
                Votre inscription à l'événement <strong>{event.title}</strong> a été confirmée.
              </p>
              
              <div className="alert alert-info">
                <h6>📧 Confirmation par email</h6>
                <p className="mb-0">
                  Un email de confirmation avec tous les détails vous a été envoyé.
                  Conservez-le précieusement, il contient votre QR code d'entrée.
                </p>
              </div>

              <div className="d-grid gap-2 col-md-6 mx-auto">
                <a href={`/events/${eventId}`} className="btn btn-primary btn-lg">
                  Voir l'événement
                </a>
                <a href="/events" className="btn btn-outline-secondary">
                  Autres événements
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default EventRegistration;